import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import FullViewportModalShell from '../../shared/components/FullViewportModalShell';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';

import type {
  EmergencyConfigurationOnAzure,
  AddEmergencyConfigRequest,
  UpdateEmergencyConfigRequest,
} from '@/../generated-client/models';
import { useEmergencyConfigCreate } from '@/hooks/emergency_config_hooks/useEmergencyConfigCreate';
import { useEmergencyConfigEdit } from '@/hooks/emergency_config_hooks/useEmergencyConfigEdit';

import { AlertsSection } from './components/EmergencyAlertsSection';
import RadioOptionBox from '@/components/form_elements/RadioOptionBox';
import BaseModal from '@/components/modals/BaseModal';
import { modalStyles, sectionStyles } from '../../modals/unified_config_modals/styles/unifiedConfigStyles';

interface CreateModeProps {
  show: boolean;
  onHide: () => void;
  config: AddEmergencyConfigRequest;
  onSave: (updated: AddEmergencyConfigRequest) => void;
  mode: 'create';
  locked?: boolean;
}

interface EditModeProps {
  show: boolean;
  onHide: () => void;
  config: EmergencyConfigurationOnAzure;
  onSave: (updated: UpdateEmergencyConfigRequest) => void;
  mode: 'edit';
  locked?: boolean;
}

type Props = CreateModeProps | EditModeProps;

export const EmergencyConfigModalBase: React.FC<Props> = ({ show, onHide, config, onSave, mode, locked }) => {
  const { t } = useTranslation();
  const createEditor = useEmergencyConfigCreate(config as AddEmergencyConfigRequest);
  const editEditor = useEmergencyConfigEdit(config as EmergencyConfigurationOnAzure);
  const editor = mode === 'create' ? createEditor : editEditor;

  const [nameError, setNameError] = useState(false);
  const [shouldForceApply, setShouldForceApply] = useState(false);
  const [showForceApplyDialog, setShowForceApplyDialog] = useState(false);

  const createLocked = mode === 'create' && Boolean(locked);

  const handleSave = () => {
    if (createLocked) return;
    if (mode === 'create' && (!editor.specName || editor.specName.trim() === '')) {
      setNameError(true);
      return;
    }
    if (mode === 'create') {
      (onSave as (u: AddEmergencyConfigRequest) => void)(createEditor.submitConfig());
    } else {
      setShouldForceApply(false);
      setShowForceApplyDialog(true);
    }
  };

  const handleForceApplyConfirm = () => {
    (onSave as (u: UpdateEmergencyConfigRequest) => void)(editEditor.submitConfig(shouldForceApply));
    setShowForceApplyDialog(false);
    setShouldForceApply(false);
  };

  const modalTitle =
    mode === 'create'
      ? t(StringKeys.CREATE_CONFIGURATION)
      : t(StringKeys.EDIT_CONFIGURATION, { configuration: editor.specName });

  const submitButtonText = mode === 'create' ? t(StringKeys.CREATE) : t(StringKeys.SAVE);

  return (
    <>
      <FullViewportModalShell open={show} onClose={onHide}>
        <Paper
          sx={{
            width: { xs: '95vw', lg: 600 },
            minWidth: { lg: 500 },
            maxWidth: { lg: 720 },
            height: 'auto',
            maxHeight: '90vh',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={modalStyles.header}>
            <Typography
              variant="h6"
              fontWeight={600}
              noWrap
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}
            >
              {modalTitle}
            </Typography>
            <IconButton onClick={onHide} sx={{ color: '#fff', flexShrink: 0 }}><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {createLocked && (
              <Box sx={{ px: 3, pt: 2, mb: 2 }}>
                <Alert severity="warning">{t(StringKeys.CONFIG_REQUIRES_RESPOND)}</Alert>
              </Box>
            )}
            {mode === 'create' && (
              <Box sx={modalStyles.nameInputSection}>
                <TextField
                  fullWidth
                  label={t(StringKeys.CONFIGURATION_NAME)}
                  placeholder={t(StringKeys.PLEASE_ENTER_A_CONFIGURATION_NAME)}
                  value={editor.specName}
                  onChange={(e) => {
                    editor.setSpecName(e.target.value);
                    if (nameError && e.target.value.trim() !== '') setNameError(false);
                  }}
                  error={nameError}
                  helperText={nameError ? t(StringKeys.CONFIGURATION_NAME_IS_REQUIRED) : ''}
                  variant="outlined"
                />
              </Box>
            )}

            <Box sx={sectionStyles.alertsSection}>
              <AlertsSection
                label={t(StringKeys.HEAD_IMPACT_DETECTION)}
                value={editor.impactDetection}
                setValue={editor.setImpactDetection}
              />
              <AlertsSection
                label={t(StringKeys.FALL_DETECTION)}
                value={editor.fallDetection}
                setValue={editor.setFallDetection}
              />
              <AlertsSection
                label={t(StringKeys.ARC_FLASH_DETECTION)}
                value={editor.arcFlashDetection}
                setValue={editor.setArcFlashDetection}
              />
            </Box>
          </Box>

          <Box sx={modalStyles.footer}>
            <Button onClick={onHide} sx={modalStyles.cancelButton}>{t(StringKeys.CANCEL)}</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSave}
              disabled={createLocked}
            >
              {submitButtonText}
            </Button>
          </Box>
        </Paper>
      </FullViewportModalShell>

      <BaseModal
        open={showForceApplyDialog}
        onClose={() => setShowForceApplyDialog(false)}
        title={modalTitle}
        onSubmit={handleForceApplyConfirm}
        submitButtonText="Apply"
        size="sm"
        sx={{ zIndex: 2100 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('choose_how_configuration_change_should_be_applied')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <RadioOptionBox
            selected={!shouldForceApply}
            onClick={() => setShouldForceApply(false)}
            label={t('require_worker_confirmation')}
            description={t('require_worker_confirmation_description')}
          />
          <RadioOptionBox
            selected={shouldForceApply}
            onClick={() => setShouldForceApply(true)}
            label={t('apply_without_worker_confirmation')}
            description={t('apply_without_worker_confirmation_description')}
          />
        </Box>
      </BaseModal>
    </>
  );
};
